import { useEffect, useState, useRef, useCallback } from "react";
import { aiCopilotService, tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { voiceApi, createRecorder } from "@/lib/voice";
import { toast } from "sonner";
import {
  Sparkles, Send, Plus, Loader2, Trash2, ThumbsUp, ThumbsDown, ShieldCheck, Info,
  MessageSquare, Lightbulb, AlertTriangle, Mic, Square, Volume2,
} from "lucide-react";

const CONF_COLOR = {
  High: "text-emerald-600 bg-emerald-500/10 border-emerald-500/30",
  Medium: "text-amber-600 bg-amber-500/10 border-amber-500/30",
  Low: "text-red-600 bg-red-500/10 border-red-500/30",
};
const STARTERS = [
  "How many open support tickets do we have?",
  "Summarise our top customers and their lifecycle stages.",
  "Which campaigns have the lowest delivery rates?",
  "What does our knowledge base say about resetting a password?",
];

function ConfidenceBadge({ label }) {
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${CONF_COLOR[label] || CONF_COLOR.Low}`} data-testid="copilot-confidence">
      <ShieldCheck className="h-3 w-3" /> {label} confidence
    </span>
  );
}

export default function CopilotPage() {
  const { user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const scrollRef = useRef(null);
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [speakingId, setSpeakingId] = useState(null);
  const recorderRef = useRef(null);
  const audioRef = useRef(null);
  const speakFromVoiceRef = useRef(false);

  useEffect(() => { voiceApi.status().then((s) => setVoiceOn(s.available)).catch(() => setVoiceOn(false)); }, []);

  const speak = useCallback(async (text, id) => {
    if (!text) return;
    try {
      if (audioRef.current) { audioRef.current.pause(); URL.revokeObjectURL(audioRef.current.src); }
      setSpeakingId(id);
      const url = await voiceApi.speak(text);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setSpeakingId(null); URL.revokeObjectURL(url); };
      await audio.play();
    } catch (e) { setSpeakingId(null); toast.error(apiErrorMessage(e.response?.data?.detail) || "Playback failed"); }
  }, []);

  const loadConvos = useCallback(() => {
    aiCopilotService.listConversations().then((d) => setConversations(d.items || [])).catch(() => {});
  }, []);

  useEffect(() => { loadConvos(); }, [loadConvos]);
  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, sending]);

  const openConversation = async (id) => {
    setActiveId(id);
    try {
      const c = await aiCopilotService.getConversation(id);
      setMessages(c.messages || []);
      if (c.scopeTenantId) setTenantId(c.scopeTenantId);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const newChat = () => { setActiveId(null); setMessages([]); setInput(""); };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg) return;
    if (isSuper && !tenantId) return toast.error("Select a tenant for the copilot first");
    setInput("");
    setMessages((p) => [...p, { role: "user", content: msg, _local: true }]);
    setSending(true);
    try {
      const r = await aiCopilotService.query({ message: msg, conversationId: activeId, tenantId: tenantId || undefined });
      setActiveId(r.conversationId);
      setMessages((p) => [...p, {
        role: "assistant", content: r.answer, confidenceLabel: r.confidenceLabel, confidence: r.confidence,
        sources: r.sources, disclaimer: r.disclaimer, suggestedActions: r.suggestedActions,
        blockedDomains: r.blockedDomains, messageId: r.messageId,
      }]);
      loadConvos();
      if (speakFromVoiceRef.current && r.answer) { speakFromVoiceRef.current = false; speak(r.answer, r.messageId); }
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
      setMessages((p) => p.filter((m) => !m._local));
    } finally { setSending(false); }
  };

  const toggleMic = async () => {
    if (recording) {
      setRecording(false);
      setVoiceBusy(true);
      try {
        const blob = await recorderRef.current?.stop();
        if (blob && blob.size > 0) {
          const { text } = await voiceApi.transcribe(blob);
          if (text?.trim()) { speakFromVoiceRef.current = true; send(text); }
          else toast.error("Didn't catch that — please try again.");
        }
      } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail) || "Transcription failed"); }
      finally { setVoiceBusy(false); }
      return;
    }
    const rec = createRecorder();
    if (!rec.supported()) return toast.error("Voice recording isn't supported in this browser");
    if (isSuper && !tenantId) return toast.error("Select a tenant for the copilot first");
    try {
      recorderRef.current = rec;
      await rec.start();
      setRecording(true);
    } catch { toast.error("Microphone access denied"); }
  };

  const giveFeedback = async (m, rating) => {
    if (!m.messageId || m._fb) return;
    try {
      await aiCopilotService.feedback(m.messageId, { rating });
      setMessages((p) => p.map((x) => x.messageId === m.messageId ? { ...x, _fb: rating } : x));
      toast.success("Thanks for the feedback");
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const removeConvo = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await aiCopilotService.deleteConversation(id);
      if (id === activeId) newChat();
      loadConvos();
    } catch (err) { toast.error(apiErrorMessage(err.response?.data?.detail)); }
  };

  return (
    <div className="flex gap-4 h-[calc(100vh-7rem)]" data-testid="copilot-page">
      {/* Conversation history */}
      <aside className="w-64 shrink-0 bg-card border border-border rounded-xl flex flex-col" data-testid="copilot-history">
        <div className="p-3 border-b border-border">
          <button onClick={newChat} className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-3 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="copilot-new-chat">
            <Plus className="h-4 w-4" /> New chat
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {conversations.length === 0 && <p className="text-xs text-muted-foreground p-3 text-center">No conversations yet</p>}
          {conversations.map((c) => (
            <button key={c.id} onClick={() => openConversation(c.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between gap-2 group transition-colors ${activeId === c.id ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"}`}
              data-testid={`copilot-convo-${c.id}`}>
              <span className="flex items-center gap-2 min-w-0"><MessageSquare className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{c.title}</span></span>
              <Trash2 onClick={(e) => removeConvo(c.id, e)} className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 hover:text-destructive shrink-0" data-testid={`copilot-delete-${c.id}`} />
            </button>
          ))}
        </div>
      </aside>

      {/* Chat area */}
      <section className="flex-1 bg-card border border-border rounded-xl flex flex-col min-w-0">
        <header className="flex items-center justify-between gap-3 px-5 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Sparkles className="h-4 w-4" /></span>
            <div>
              <h2 className="text-sm font-bold text-foreground">ConnectIQ Copilot</h2>
              <p className="text-xs text-muted-foreground">Read-only · answers grounded in your permitted data</p>
            </div>
          </div>
          {isSuper && (
            <select value={tenantId} onChange={(e) => { setTenantId(e.target.value); newChat(); }} className="bg-background border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" data-testid="copilot-tenant-select">
              <option value="">Select tenant…</option>
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
            </select>
          )}
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="copilot-messages">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center" data-testid="copilot-empty">
              <span className="h-14 w-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-4"><Sparkles className="h-7 w-7" /></span>
              <h3 className="text-lg font-bold text-foreground mb-1">Ask anything about your data</h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-md">{isSuper && !tenantId ? "Select a tenant above to begin." : "Customers, CRM, tickets, campaigns and your knowledge base."}</p>
              <div className="grid sm:grid-cols-2 gap-2 w-full max-w-xl">
                {STARTERS.map((s, i) => (
                  <button key={i} onClick={() => send(s)} disabled={sending || (isSuper && !tenantId)}
                    className="text-left text-sm border border-border rounded-lg px-3 py-2.5 hover:border-primary/50 hover:bg-muted/40 disabled:opacity-50 transition-colors flex items-start gap-2" data-testid={`copilot-starter-${i}`}>
                    <Lightbulb className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" /> {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            m.role === "user" ? (
              <div key={i} className="flex justify-end" data-testid="copilot-user-msg">
                <div className="bg-primary text-primary-foreground rounded-2xl rounded-br-sm px-4 py-2.5 text-sm max-w-[75%]">{m.content}</div>
              </div>
            ) : (
              <div key={i} className="flex justify-start" data-testid="copilot-assistant-msg">
                <div className="bg-muted/50 border border-border rounded-2xl rounded-bl-sm px-4 py-3 text-sm max-w-[80%] space-y-2.5">
                  <p className="text-foreground whitespace-pre-wrap">{m.content}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    {m.confidenceLabel && <ConfidenceBadge label={m.confidenceLabel} />}
                    {(m.sources || []).slice(0, 4).map((s, j) => (
                      <span key={j} className="text-xs text-muted-foreground bg-background border border-border rounded-full px-2 py-0.5">{s}</span>
                    ))}
                  </div>
                  {m.blockedDomains?.length > 0 && (
                    <p className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Some requested data ({m.blockedDomains.join(", ")}) is outside your permissions and was excluded.</p>
                  )}
                  {m.suggestedActions?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1" data-testid="copilot-suggested-actions">
                      {m.suggestedActions.map((a) => (
                        <span key={a.id} className="text-xs border border-dashed border-border text-muted-foreground rounded-full px-2 py-0.5" title="Suggestion placeholder — not executed">{a.title}</span>
                      ))}
                    </div>
                  )}
                  {m.disclaimer && (
                    <p className="text-[11px] text-muted-foreground flex items-start gap-1 pt-1 border-t border-border"><Info className="h-3 w-3 mt-0.5 shrink-0" /> {m.disclaimer}</p>
                  )}
                  {m.messageId && (
                    <div className="flex items-center gap-1 pt-0.5">
                      <button onClick={() => giveFeedback(m, "up")} className={`p-1 rounded-md hover:bg-background ${m._fb === "up" ? "text-emerald-600" : "text-muted-foreground"}`} data-testid={`copilot-thumbsup-${m.messageId}`}><ThumbsUp className="h-3.5 w-3.5" /></button>
                      <button onClick={() => giveFeedback(m, "down")} className={`p-1 rounded-md hover:bg-background ${m._fb === "down" ? "text-red-600" : "text-muted-foreground"}`} data-testid={`copilot-thumbsdown-${m.messageId}`}><ThumbsDown className="h-3.5 w-3.5" /></button>
                      {voiceOn && (
                        <button onClick={() => speak(m.content, m.messageId)} className={`p-1 rounded-md hover:bg-background ${speakingId === m.messageId ? "text-primary" : "text-muted-foreground"}`} title="Read aloud" data-testid={`copilot-speak-${m.messageId}`}>
                          {speakingId === m.messageId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Volume2 className="h-3.5 w-3.5" />}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          ))}
          {sending && (
            <div className="flex justify-start" data-testid="copilot-thinking">
              <div className="bg-muted/50 border border-border rounded-2xl rounded-bl-sm px-4 py-3 text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Thinking…
              </div>
            </div>
          )}
        </div>

        <footer className="p-4 border-t border-border">
          <div className="flex items-end gap-2">
            <textarea value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1} placeholder={isSuper && !tenantId ? "Select a tenant to begin…" : "Ask about customers, CRM, tickets, campaigns or knowledge…"}
              disabled={sending || (isSuper && !tenantId)}
              className="flex-1 resize-none bg-background border border-border rounded-xl px-4 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60" data-testid="copilot-input" />
            {voiceOn && (
              <button onClick={toggleMic} disabled={sending || voiceBusy || (isSuper && !tenantId)}
                className={`inline-flex items-center justify-center rounded-xl h-11 w-11 shrink-0 transition-colors disabled:opacity-50 ${recording ? "bg-red-500 text-white animate-pulse" : "bg-muted text-foreground hover:bg-muted/70"}`}
                title={recording ? "Stop & send" : "Ask by voice"} data-testid="copilot-mic">
                {voiceBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </button>
            )}
            <button onClick={() => send()} disabled={sending || !input.trim() || (isSuper && !tenantId)}
              className="inline-flex items-center justify-center bg-primary text-primary-foreground rounded-xl h-11 w-11 hover:bg-primary/90 disabled:opacity-50 shrink-0" data-testid="copilot-send">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2 text-center">{recording ? "Listening… tap stop to send." : "Copilot is read-only and may be inaccurate. It only accesses data you are permitted to see."}</p>
        </footer>
      </section>
    </div>
  );
}
